import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/** Overlays that are open, last one on top. Escape and Tab belong to the top one only. */
const open: symbol[] = [];
let locks = 0;
let savedOverflow = '';
let savedPadding = '';

function lockScroll() {
  if (locks++ > 0) return;
  const body = document.body;
  savedOverflow = body.style.overflow;
  savedPadding = body.style.paddingRight;
  // Hiding the scrollbar would widen the page and shift it: keep its width.
  const gap = window.innerWidth - document.documentElement.clientWidth;
  if (gap > 0) body.style.paddingRight = `${gap}px`;
  body.style.overflow = 'hidden';
}

function unlockScroll() {
  if (--locks > 0) return;
  document.body.style.overflow = savedOverflow;
  document.body.style.paddingRight = savedPadding;
}

function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) =>
      !el.closest('[hidden], [aria-hidden="true"], [inert]') &&
      el.style.display !== 'none' &&
      el.style.visibility !== 'hidden'
  );
}

interface OverlayOptions {
  /** The element that holds the overlay's content. */
  panelRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  /** Takes the focus on open. Defaults to the panel itself, so Enter on open presses nothing. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** False while the overlay is closing or hidden: nothing is held then. */
  active?: boolean;
}

/**
 * What every dialog and side panel does the same way:
 * - Escape closes the top overlay only;
 * - Tab and Shift+Tab stay inside the panel;
 * - the focus goes in on open and back to the control that opened it on close;
 * - the page behind does not scroll.
 */
export function useOverlay({ panelRef, onClose, initialFocus, active = true }: OverlayOptions) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const initialRef = useRef(initialFocus);
  initialRef.current = initialFocus;

  useEffect(() => {
    if (!active) return;
    const panel = panelRef.current;
    if (!panel) return;
    const token = Symbol('overlay');
    const opener = document.activeElement as HTMLElement | null;
    open.push(token);
    lockScroll();

    (initialRef.current?.current ?? panel).focus();

    const onKey = (e: KeyboardEvent) => {
      if (open[open.length - 1] !== token) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusableIn(panel);
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const head = items[0];
      const tail = items[items.length - 1];
      const current = document.activeElement;
      if (!panel.contains(current) || current === panel) {
        e.preventDefault();
        (e.shiftKey ? tail : head).focus();
      } else if (e.shiftKey && current === head) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && current === tail) {
        e.preventDefault();
        head.focus();
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      open.splice(open.indexOf(token), 1);
      unlockScroll();
      if (opener?.isConnected) opener.focus();
    };
  }, [active, panelRef]);
}
